import React, { useState } from "react";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Button,
	Typography,
	Chip,
	Stack,
} from "@mui/material";
import { useTranslationContext } from "../../../../hooks/translations/translationsProvider";
import { getIdentityUsers } from "../../../../hooks/actions/identityManagement";
import { UserWithRoles } from "../../../../models/identity/IdentityManagement";
import {
	bodyCellSx,
	headCellSx,
	primaryButtonSx,
	roleChipSx,
	searchFieldSx,
	tableContainerSx,
} from "./identityStyles";

interface UsersListProps {
	onSelectUser: (user: UserWithRoles) => void;
}

const UsersList: React.FC<UsersListProps> = ({ onSelectUser }) => {
	const { t } = useTranslationContext();
	const [search, setSearch] = useState("");
	const [items, setItems] = useState<UserWithRoles[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [searched, setSearched] = useState(false);

	const load = async () => {
		setError(null);
		setLoading(true);
		try {
			const res = await getIdentityUsers(search.trim(), 0, 50);
			setItems(res.items ?? []);
			setSearched(true);
		} catch (e: any) {
			setError(t("Errore nella ricerca utenti"));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			{error && (
				<Typography color="error" sx={{ mb: 2 }}>
					{error}
				</Typography>
			)}

			<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
				<TextField
					size="small"
					placeholder={t("Cerca per email, username o id")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && load()}
					sx={searchFieldSx}
				/>
				<Button variant="contained" onClick={load} sx={primaryButtonSx}>
					{t("Cerca")}
				</Button>
			</Box>

			<TableContainer component={Paper} sx={tableContainerSx}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={headCellSx}>{t("Email")}</TableCell>
							<TableCell sx={headCellSx}>{t("Username")}</TableCell>
							<TableCell sx={headCellSx}>{t("Ruoli")}</TableCell>
							<TableCell sx={headCellSx} align="right">{t("Azioni")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell sx={bodyCellSx} colSpan={4}>{t("Caricamento...")}</TableCell>
							</TableRow>
						) : items.length === 0 ? (
							<TableRow>
								<TableCell sx={bodyCellSx} colSpan={4}>
									{searched ? t("Nessun utente trovato") : t("Effettua una ricerca per visualizzare gli utenti")}
								</TableCell>
							</TableRow>
						) : (
							items.map((u) => (
								<TableRow key={u.id} hover>
									<TableCell sx={bodyCellSx}>{u.email}</TableCell>
									<TableCell sx={bodyCellSx}>{u.userName}</TableCell>
									<TableCell sx={bodyCellSx}>
										<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
											{u.roles.length === 0
												? "-"
												: u.roles.map((r) => <Chip key={r} size="small" label={r} sx={roleChipSx} />)}
										</Stack>
									</TableCell>
									<TableCell sx={bodyCellSx} align="right">
										<Button size="small" variant="contained" onClick={() => onSelectUser(u)} sx={primaryButtonSx}>
											{t("Gestisci ruoli")}
										</Button>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export default UsersList;
